import {CheckoutFactory, ICartPosition} from "@connox/typescript-library";
import * as Sentry from "@sentry/browser";
import * as React from "react";
import * as ReactDOM from "react-dom";
import {getShopId} from "../../lib/getShopId";
import CartWrapperModal from "../../lib/ModalWindow/CartWrapperModal";
import window from "../../lib/WindowExtended";
import {isCart, useUnsafeCartContext} from "../CartContextProvider/CartContextProvider";
import {useEffect, useState} from "react";

const Badge: React.FC<{count: number}> = (props) => {
  if (props.count === 0) {
    return null;
  }

  return <span className="icon-badge icon-badge--green">{props.count}</span>;
};

const CartIcon: React.FC = () => {
  const context = useUnsafeCartContext();
  const params = useState(new URLSearchParams(location.search))[0];

  // this effect will control adding cart view and checkout view to browser history.
  useEffect(() => {
    if (params.get("view") === "cart") {
      context?.setIsCartVisible(true);
    }

    const onPopStateNocart = () => {
      if (window.location.search.includes("cart")) {
        context?.setIsCartVisible(true);
      } else {
        context?.setIsCartVisible(false);
      }
    };

    if (!isCart(context)) {
      window.addEventListener("popstate", onPopStateNocart);

      // this will remove checkout view param from history in case there is no cart.
      if (params.get("view") === "checkout") {
        updateUrlParams(null);
      }

      return;
    }

    window.removeEventListener("popstate", onPopStateNocart);
    const invalidPositions = context.cart.positions.filter((pos) => !!pos.messages.find((msg) => msg.type === "error"));

    if (params.get("view") === "checkout") {
      // in case there are no positions in cart; the customer be redirected from checkout to the shop
      if (!context.cart.positions.length) {
        updateUrlParams(null);
      } else {
        context.setIsCartVisible(true);
        context.setIsIntoCheckout(!invalidPositions.length);

        // in case there is a cart error the customer will be redirected from checkout to cart view.
        updateUrlParams(invalidPositions.length ? "cart" : "checkout", "replace");
      }
    }

    // this function will get called after every click on back and forward buttons in the browser
    const onPopState = async () => {
      // this will force reloading the page after finishing the purchase and click back button in browser
      await reloadOnCheckoutComplete();
      const newParams = new URLSearchParams(location.search);
      const newInvalidPositions = context.cart.positions.filter(
        (pos) => !!pos.messages.find((msg) => msg.type === "error"),
      );

      if (newParams.get("view") === "cart") {
        context.setIsCartVisible(true);
        context.setIsIntoCheckout(false);
      } else if (newParams.get("view") === "checkout") {
        context.setIsCartVisible(true);
        context.setIsIntoCheckout(!newInvalidPositions.length);
        if (newInvalidPositions.length) {
          updateUrlParams("cart", "replace");
        }
      } else {
        context.setIsCartVisible(false);
        context.setIsIntoCheckout(false);
      }
    };

    const voidOnPopState = () => void onPopState();

    window.addEventListener("popstate", voidOnPopState);

    return () => {
      window.removeEventListener("popstate", voidOnPopState);
      window.removeEventListener("popstate", onPopStateNocart);
    };
  }, [isCart(context)]);

  const reloadOnCheckoutComplete = async () => {
    if (isCart(context)) {
      await CheckoutFactory.generateCheckout(getShopId(), context.cart).then((checkout) => {
        if (checkout.isCompleted) {
          updateUrlParams(null);
          window.location.reload();
        }
      }, Sentry.captureException);
    }
  };

  const closeFunction = async () => {
    await reloadOnCheckoutComplete();
    updateUrlParams(null);
    if (context) {
      context.setIsCartVisible(false);
    }
  };

  if (!context) {
    // FYI if this happens it might be an error, so maybe log it, but mvp`ing through this
    return null;
  }

  const items =
    isCart(context) &&
    context.cart.positions.filter((position: ICartPosition) => {
      return position.reference && position.reference.itemId && !!position.item;
    });

  return (
    <div
      onClick={() => {
        context.setIsCartVisible(true);
        updateUrlParams("cart");
      }}
    >
      <Badge count={items ? items.length : 0} />
      <span className="icon icon-link icon-cart icon-size--26">Shopping cart</span>
      <CartWrapperModal isCartVisible={context.isCartVisible} closeFunction={() => void closeFunction()} />
    </div>
  );
};

const CartIconPortal: React.FC<{targetNode: HTMLSpanElement | null; root: HTMLSpanElement | null}> = (props) => {
  const {targetNode, root} = props;

  React.useEffect(() => {
    if (!root || !targetNode) {
      return;
    }

    root.style.display = "inline-block";
    root.style.cursor = "pointer";
    targetNode.replaceWith(root);
  }, []);

  if (!root || !targetNode) {
    return null;
  }

  return ReactDOM.createPortal(<CartIcon />, root);
};

type ShopStateType = {
  navOpened?: number;
  view?: string;
  cart?: boolean;
  checkout?: boolean;
};

export const updateUrlParams = (view: string | null, mode: "replace" | "push" = "push"): void => {
  const {origin, pathname, search, hash} = window.location;
  const usp = new URLSearchParams(search);
  if (view) {
    usp.set("view", view);
  } else {
    usp.delete("view");
  }

  // this state will prevent stacking same urls in history.
  let newState: {cart?: boolean; checkout?: boolean} = {};

  if (view === "cart" || view === "checkout") {
    newState = {cart: true};
  }

  if (view === "checkout") {
    newState = {...newState, checkout: true};
  }

  const state = (history.state || {}) as ShopStateType;
  const stateData = {...state, ...newState} as ShopStateType;
  const stateUrl = origin + pathname + (usp.toString() ? "?" + usp.toString() : "") + hash;

  if (view && !Object.keys(state).includes(view) && mode === "push") {
    window.history.pushState(stateData, view, stateUrl);
  } else {
    window.history.replaceState(stateData, "", stateUrl);
  }
};

export default CartIconPortal;
